/**
 * ConsultantBrandingContext.tsx
 * Provides consultant branding data (logo, company name) across the app.
 * Used to display co-branding in the header for both consultants and their clients.
 */

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import api from '../services/api';

// ============================================================================
// TYPES
// ============================================================================

interface ConsultantBranding {
  company_display_name: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  company_tagline: string;
}

interface ConsultantBrandingContextType {
  branding: ConsultantBranding | null;
  isLoading: boolean;
  refreshBranding: () => Promise<void>;
  setBrandingFromDashboard: (branding: ConsultantBranding) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ConsultantBrandingContext = createContext<ConsultantBrandingContextType | undefined>(undefined);

export const useConsultantBranding = () => {
  const context = useContext(ConsultantBrandingContext);
  if (context === undefined) {
    throw new Error('useConsultantBranding must be used within a ConsultantBrandingProvider');
  }
  return context;
};

// ============================================================================
// PROVIDER
// ============================================================================

interface ConsultantBrandingProviderProps {
  children: ReactNode;
}

export const ConsultantBrandingProvider: React.FC<ConsultantBrandingProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { consultantCompanies, clientCompanies, isConsultantUser } = useCompany();
  
  const [branding, setBranding] = useState<ConsultantBranding | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedConsultantIdRef = useRef<number | null>(null);

  // Determine which company ID to use for branding request
  // For consultants: use their consultant company ID
  // For clients: use their client company ID (backend will resolve to parent consultant)
  const getCompanyIdForBranding = (): number | null => {
    // If user is a consultant, use their consultant company
    if (consultantCompanies.length > 0) {
      return consultantCompanies[0].id;
    }
    
    // If user is a client, use their client company ID
    // The backend /branding/ endpoint will resolve to the parent consultant
    if (clientCompanies.length > 0) {
      return clientCompanies[0].id;
    }
    
    return null;
  };

  // Load branding when user is authenticated
  useEffect(() => {
    const companyId = getCompanyIdForBranding();
    
    if (isAuthenticated && user && companyId) {
      // Only reload if company ID changed
      if (lastLoadedConsultantIdRef.current !== companyId) {
        loadBranding(companyId);
        lastLoadedConsultantIdRef.current = companyId;
      }
    } else if (!isAuthenticated || !user) {
      setBranding(null);
      lastLoadedConsultantIdRef.current = null;
    }
  }, [isAuthenticated, user, consultantCompanies, clientCompanies]);

  const loadBranding = async (companyId: number) => {
    try {
      setIsLoading(true);
      // Use the /branding/ endpoint which handles both consultant and client companies
      const response = await api.get(`/companies/${companyId}/branding/`);
      
      if (response.data) {
        setBranding({
          company_display_name: response.data.company_display_name || '',
          logo_url: response.data.logo_url || '',
          primary_color: response.data.primary_color || '#E53935',
          secondary_color: response.data.secondary_color || '#212121',
          company_tagline: response.data.company_tagline || '',
        });
        console.log('✅ Consultant branding loaded for company ID', companyId, ':', response.data.company_display_name || '(logo only)');
      }
    } catch (err: any) {
      // Settings might not exist yet - use empty defaults
      console.log('ℹ️ No branding settings found for company ID', companyId);
      setBranding({
        company_display_name: '',
        logo_url: '',
        primary_color: '#E53935',
        secondary_color: '#212121',
        company_tagline: '',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshBranding = async () => {
    const companyId = getCompanyIdForBranding();
    if (companyId) {
      await loadBranding(companyId);
    }
  };

  // Allow setting branding data directly (e.g., from combined dashboard endpoint)
  const setBrandingFromDashboard = (brandingData: ConsultantBranding) => {
    setBranding(brandingData);
    console.log('✅ Branding set from dashboard data (skipped separate API call)');
  };

  const value: ConsultantBrandingContextType = {
    branding,
    isLoading,
    refreshBranding,
    setBrandingFromDashboard,
  };

  return (
    <ConsultantBrandingContext.Provider value={value}>
      {children}
    </ConsultantBrandingContext.Provider>
  );
};

